import React, {useEffect} from "react"
import {useParams} from "react-router-dom"
import {useNavigate} from "react-router-dom"
import {startCase} from "lodash"
import classNames from "classnames"
import moment from "moment"
import numeral from "numeral"
import {useModal} from "react-modal-hook"
import {useToasts} from "react-toast-notifications"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useAccessToken} from "../../services/auth"
import {hasPermission} from "../../services/permission"
import {getIntent, cancelIntent, IntentStatus, PaymentIntent} from "../../resources/intents"
import {ErrorDocument, OkDocument} from "../../resources/common"
import TitleBar, {TitleBarAction} from "../../components/TitleBar/TitleBar"
import {MainSection} from "../../containers/MainSection/MainSection"
import HorizontalField from "../../components/HorizontalField/HorizontalField"
import {ConfirmationModal} from "../../components/ConfirmationModal/ConfirmationModal"
import Icon from "../../components/Icon/Icon"

function intentStatusClass(status: string): string {
	switch (status) {
		case IntentStatus.Approved:
		case IntentStatus.Completed:
			return "status-success"
		case IntentStatus.Processing:
			return "status-pending"
		case IntentStatus.Declined:
		case IntentStatus.Failed:
		case IntentStatus.Cancelled:
			return "status-danger"
		default:
			return "status-neutral"
	}
}

export default function ManagedSolutionsIntentPage() {
	const {intentId = ""} = useParams<{intentId: string}>()
	const accessToken = useAccessToken()
	const navigate = useNavigate()
	const [refreshToken, refresh] = useRefreshToken()
	const asyncResult = useAsyncResult(() => getIntent(accessToken, intentId), [intentId, refreshToken])
	const [cancelState, cancel] = useAsyncResultIdle(cancelIntent)
	const {addToast} = useToasts()
	const canViewPayments = hasPermission("payment", "get")

	useEffect(() => {
		cancelState.match(
			() => null,
			() => null,
			() => refresh(),
			(e: ErrorDocument) =>
				addToast(`${e.errors[0]?.title ?? "Error"} - ${e.errors[0]?.detail ?? ""}`, {appearance: "error"})
		)
	}, [cancelState])

	return (
		<AsyncResultComponent asyncResult={asyncResult}>
			{({value: doc}: {value: OkDocument<PaymentIntent>}) => {
				const intent = doc.data
				const {
					title,
					description,
					amount,
					counterparty,
					paymentType,
					status,
					approvedAt,
					cancelledAt,
					declinedAt,
					failureReason,
					createdAt,
					updatedAt,
				} = intent.attributes

				const customerId = intent.relationships?.customer?.data.id
				const accountId = intent.relationships?.account?.data.id
				const paymentId = intent.relationships?.payment?.data.id
				const counterpartyId = intent.relationships?.counterparty?.data.id
				const isCancellable = status === IntentStatus.Created

				return (
					<CancelIntentWrapper isCancellable={isCancellable} onCancel={() => cancel(accessToken, intent.id)}>
						{(showCancelModal) => (
							<>
								<TitleBar
									title={`Intent ${intent.id}`}
									breadcrumbs={[{text: "Suggested Actions", path: "/managed-solutions/intents"}]}
									status={
										<span className={classNames("status-badge", intentStatusClass(status))} style={{marginLeft: "8px"}}>
											{startCase(status)}
										</span>
									}
								>
									{isCancellable && (
										<TitleBarAction>
											<button className="button is-danger is-outlined" onClick={showCancelModal}>
												<span className="icon">
													<Icon icon="disable-delete--interface-essential" size={12} />
												</span>
												<span>Cancel</span>
											</button>
										</TitleBarAction>
									)}
								</TitleBar>

								<MainSection>
									<div className="columns">
										<div className="column is-6">
											<div className="card">
												<div className="card-header">
													<p className="card-header-title">Intent Details</p>
												</div>
												<div className="card-content">
													<HorizontalField label="ID">
														<input type="text" className="input is-static" readOnly value={intent.id} />
													</HorizontalField>
													<HorizontalField label="Title">
														<input type="text" className="input is-static" readOnly value={title} />
													</HorizontalField>
													{description && (
														<HorizontalField label="Description">
															<input type="text" className="input is-static" readOnly value={description} />
														</HorizontalField>
													)}
													<HorizontalField label="Amount">
														<input
															type="text"
															className="input is-static has-text-weight-semibold"
															readOnly
															value={numeral(amount / 100).format("$0,0.00")}
														/>
													</HorizontalField>
													{paymentType && (
														<HorizontalField label="Payment Type">
															<input type="text" className="input is-static" readOnly value={startCase(paymentType)} />
														</HorizontalField>
													)}
													<HorizontalField label="Created At">
														<input
															type="text"
															className="input is-static"
															readOnly
															value={moment(createdAt).format("L LT")}
														/>
													</HorizontalField>
													<HorizontalField label="Updated At">
														<input
															type="text"
															className="input is-static"
															readOnly
															value={moment(updatedAt).format("L LT")}
														/>
													</HorizontalField>
													{approvedAt && (
														<HorizontalField label="Approved At">
															<input
																type="text"
																className="input is-static"
																readOnly
																value={moment(approvedAt).format("L LT")}
															/>
														</HorizontalField>
													)}
													{cancelledAt && (
														<HorizontalField label="Cancelled At">
															<input
																type="text"
																className="input is-static"
																readOnly
																value={moment(cancelledAt).format("L LT")}
															/>
														</HorizontalField>
													)}
													{declinedAt && (
														<HorizontalField label="Declined At">
															<input
																type="text"
																className="input is-static"
																readOnly
																value={moment(declinedAt).format("L LT")}
															/>
														</HorizontalField>
													)}
													{failureReason && (
														<HorizontalField label="Failure Reason">
															<input
																type="text"
																className="input is-static has-text-danger"
																readOnly
																value={failureReason}
															/>
														</HorizontalField>
													)}
												</div>
											</div>
										</div>

										<div className="column is-6">
											{counterparty && (
												<div className="card">
													<div className="card-header">
														<p className="card-header-title">Counterparty</p>
													</div>
													<div className="card-content">
														{counterparty.name && (
															<HorizontalField label="Name">
																<input type="text" className="input is-static" readOnly value={counterparty.name} />
															</HorizontalField>
														)}
														{(counterparty.routingNumber || counterparty.routingNumberLast4) && (
															<HorizontalField label="Routing Number">
																<input
																	type="text"
																	className="input is-static"
																	readOnly
																	value={counterparty.routingNumber ?? `*****${counterparty.routingNumberLast4}`}
																/>
															</HorizontalField>
														)}
														{(counterparty.accountNumber || counterparty.accountNumberLast4) && (
															<HorizontalField label="Account Number">
																<input
																	type="text"
																	className="input is-static"
																	readOnly
																	value={counterparty.accountNumber ?? `*****${counterparty.accountNumberLast4}`}
																/>
															</HorizontalField>
														)}
													</div>
												</div>
											)}

											{(customerId || accountId || paymentId || counterpartyId) && (
												<div className="card" style={{marginTop: "1rem"}}>
													<div className="card-header">
														<p className="card-header-title">Relationships</p>
													</div>
													<div className="card-content">
														{customerId && (
															<HorizontalField label="Customer">
																<a
																	className="entity-link"
																	href={`/managed-solutions/customers/${customerId}`}
																	onClick={(e) => {
																		e.preventDefault()
																		navigate(`/managed-solutions/customers/${customerId}`)
																	}}
																>
																	{customerId}
																</a>
															</HorizontalField>
														)}
														{accountId && (
															<HorizontalField label="Account">
																<a
																	className="entity-link"
																	href={`/managed-solutions/accounts/${accountId}`}
																	onClick={(e) => {
																		e.preventDefault()
																		navigate(`/managed-solutions/accounts/${accountId}`)
																	}}
																>
																	{accountId}
																</a>
															</HorizontalField>
														)}
														{paymentId && (
															<HorizontalField label="Payment">
																{canViewPayments ? (
																	<a
																		className="entity-link"
																		href={`/payment/${paymentId}`}
																		onClick={(e) => {
																			e.preventDefault()
																			navigate(`/payment/${paymentId}`)
																		}}
																	>
																		{paymentId}
																	</a>
																) : (
																	<input type="text" className="input is-static" readOnly value={paymentId} />
																)}
															</HorizontalField>
														)}
														{counterpartyId && (
															<HorizontalField label="Counterparty">
																<input type="text" className="input is-static" readOnly value={counterpartyId} />
															</HorizontalField>
														)}
													</div>
												</div>
											)}
										</div>
									</div>
								</MainSection>
							</>
						)}
					</CancelIntentWrapper>
				)
			}}
		</AsyncResultComponent>
	)
}

function CancelIntentWrapper({
	isCancellable,
	onCancel,
	children,
}: {
	isCancellable: boolean
	onCancel: () => void
	children: (showCancelModal: () => void) => React.ReactNode
}) {
	const [showCancelModal, hideCancelModal] = useModal(() => (
		<ConfirmationModal
			close={hideCancelModal}
			onSubmit={() => {
				onCancel()
				hideCancelModal()
			}}
			title="Cancel Suggested Action"
			cancelButtonClassname="button is-white"
			okButtonClassname="button is-danger is-outlined"
		>
			Are you sure you want to cancel this suggested action? This cannot be undone.
		</ConfirmationModal>
	))

	return <>{children(isCancellable ? showCancelModal : () => null)}</>
}
